import { useState, useEffect, useCallback } from 'react';
import api from '../services/api';

const SYMPTOM_LABELS = {
  bloating: 'Bloating', cramps: 'Cramps', gas: 'Gas',
  nausea: 'Nausea', urgency: 'Urgency', fatigue: 'Fatigue',
  blood: 'Blood', mucus: 'Mucus'
};
const SYMPTOM_OPTIONS = Object.keys(SYMPTOM_LABELS).map(val => ({ val, label: SYMPTOM_LABELS[val] }));

const BRISTOL_LABELS = {
  '1': 'Type 1 · Hard lumps', '2': 'Type 2 · Lumpy', '3': 'Type 3 · Cracked',
  '4': 'Type 4 · Smooth', '5': 'Type 5 · Soft blobs', '6': 'Type 6 · Mushy',
  '7': 'Type 7 · Liquid',
  'mild': '😊 Easy', 'moderate': '😐 Meh', 'severe': '😣 Uh-oh',
};
const BRISTOL_EDIT_OPTIONS = [
  { val: '1', label: '1 · Hard lumps' }, { val: '2', label: '2 · Lumpy' },
  { val: '3', label: '3 · Cracked' }, { val: '4', label: '4 · Smooth' },
  { val: '5', label: '5 · Soft blobs' }, { val: '6', label: '6 · Mushy' },
  { val: '7', label: '7 · Liquid' },
];

const DAY_LABELS = ['M', 'T', 'W', 'T', 'F', 'S', 'S'];
const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'];

function getWeekDays() {
  const today = new Date();
  const day = today.getDay();
  const mondayOffset = day === 0 ? -6 : 1 - day;
  const monday = new Date(today);
  monday.setDate(today.getDate() + mondayOffset);
  const days = [];
  for (let i = 0; i < 7; i++) {
    const date = new Date(monday);
    date.setDate(monday.getDate() + i);
    days.push(date);
  }
  return days;
}

function getMonthGrid(year, month) {
  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);
  const startOffset = (firstDay.getDay() + 6) % 7;
  const cells = [];
  for (let i = 0; i < startOffset; i++) cells.push(null);
  for (let d = 1; d <= lastDay.getDate(); d++) {
    cells.push(new Date(year, month, d));
  }
  return cells;
}

function isSameDay(a, b) {
  if (!a || !b) return false;
  return a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate();
}

const CHECKIN_LABELS = {
  stressLevel: { label: 'Stress', values: { low: 'Low', medium: 'Med', high: 'High' } },
  sleepQuality: { label: 'Sleep', values: { great: 'Great', ok: 'OK', bad: 'Bad' } },
  hydration: { label: 'Water', values: { good: 'Good', low: 'Low' } },
  exerciseLevel: { label: 'Exercise', values: { none: 'None', light: 'Light', intense: 'Intense' } },
  alcoholIntake: { label: 'Alcohol', values: { none: 'None', some: 'Some', heavy: 'Heavy' } },
  medicationTaken: { label: 'Meds', values: { true: 'Yes', false: 'No' } },
};

function History() {
  const [entries, setEntries] = useState([]);
  const [dailyLogs, setDailyLogs] = useState({});
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [nextCursor, setNextCursor] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [calendarExpanded, setCalendarExpanded] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [editSeverity, setEditSeverity] = useState(null);
  const [editSymptoms, setEditSymptoms] = useState([]);
  const [actionLoading, setActionLoading] = useState(false);

  const today = new Date();
  const [viewMonth, setViewMonth] = useState(today.getMonth());
  const [viewYear, setViewYear] = useState(today.getFullYear());

  const fetchHistory = useCallback(async (cursor = null) => {
    try {
      if (cursor) {
        setLoadingMore(true);
      } else {
        setLoading(true);
      }

      const params = {};
      if (cursor) params.cursor = cursor;

      const res = await api.get('/history', { params });

      if (cursor) {
        setEntries(prev => [...prev, ...res.data.items]);
      } else {
        setEntries(res.data.items);
      }
      setNextCursor(res.data.nextCursor);
      setHasMore(res.data.hasMore);
    } catch (error) {
      console.error('Failed to fetch history:', error);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, []);

  useEffect(() => {
    fetchHistory();
  }, [fetchHistory]);

  const fetchDateHistory = async (date) => {
    try {
      setLoading(true);
      const dateStr = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
      const tzOffset = new Date().getTimezoneOffset();
      const res = await api.get('/history', { params: { date: dateStr, tzOffset } });
      setEntries(res.data.items);
      setNextCursor(null);
      setHasMore(false);
    } catch (error) {
      console.error('Failed to fetch history:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    const todayDate = new Date();
    const yesterday = new Date(todayDate);
    yesterday.setDate(yesterday.getDate() - 1);

    if (date.toDateString() === todayDate.toDateString()) {
      return 'Today';
    } else if (date.toDateString() === yesterday.toDateString()) {
      return 'Yesterday';
    }
    return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  };

  const formatTime = (dateStr) => {
    return new Date(dateStr).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  };

  const groupByDate = (items) => {
    const groups = {};
    items.forEach(item => {
      const date = formatDate(item.timestamp);
      if (!groups[date]) groups[date] = [];
      groups[date].push(item);
    });
    return groups;
  };

  const handleDayClick = (date) => {
    if (selectedDate && isSameDay(selectedDate, date)) {
      setSelectedDate(null);
      fetchHistory();
    } else {
      setSelectedDate(date);
      fetchDateHistory(date);
    }
  };

  const handleMonthDayClick = (date) => {
    if (!date) return;
    setSelectedDate(date);
    setCalendarExpanded(false);
    fetchDateHistory(date);
  };

  const prevMonth = () => {
    if (viewMonth === 0) {
      setViewMonth(11);
      setViewYear(viewYear - 1);
    } else {
      setViewMonth(viewMonth - 1);
    }
  };

  const nextMonth = () => {
    if (viewMonth === 11) {
      setViewMonth(0);
      setViewYear(viewYear + 1);
    } else {
      setViewMonth(viewMonth + 1);
    }
  };

  const handleCardClick = (item) => {
    if (editingId) return;
    if (expandedId === item.id) {
      setExpandedId(null);
      setConfirmDeleteId(null);
    } else {
      setExpandedId(item.id);
      setConfirmDeleteId(null);
    }
  };

  const handleDelete = async (item) => {
    setActionLoading(true);
    try {
      const endpoint = item.type === 'meal' ? '/meals' : '/poops';
      await api.delete(`${endpoint}/${item.id}`);
      setEntries(prev => prev.filter(e => e.id !== item.id));
      setExpandedId(null);
      setConfirmDeleteId(null);
    } catch (error) {
      console.error('Delete failed:', error);
    } finally {
      setActionLoading(false);
    }
  };

  const parseSymptoms = (item) => {
    if (!item.symptoms) return [];
    if (Array.isArray(item.symptoms)) return item.symptoms;
    try { return JSON.parse(item.symptoms); } catch { return []; }
  };

  const toggleEditSymptom = (symptom) => {
    setEditSymptoms(prev =>
      prev.includes(symptom) ? prev.filter(s => s !== symptom) : [...prev, symptom]
    );
  };

  const startEdit = (item) => {
    setEditingId(item.id);
    if (item.type === 'poop') {
      setEditSeverity(item.severity || null);
      setEditSymptoms(parseSymptoms(item));
    }
  };

  const savePoopEdit = async (item) => {
    setActionLoading(true);
    try {
      await api.put(`/poops/${item.id}`, {
        severity: editSeverity,
        symptoms: editSymptoms
      });
      setEntries(prev => prev.map(e =>
          e.id === item.id ? { ...e, severity: editSeverity, symptoms: JSON.stringify(editSymptoms) } : e
        ));
      setEditingId(null);
      setExpandedId(null);
    } catch (error) {
      console.error('Update failed:', error);
    } finally {
      setActionLoading(false);
    }
  };

  const cancelEdit = () => {
    setEditingId(null);
  };

  // Fetch daily logs for dates visible in entries
  useEffect(() => {
    if (entries.length === 0) return;
    const dates = new Set();
    entries.forEach(item => {
      const d = new Date(item.timestamp);
      dates.add(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`);
    });
    const newDates = [...dates].filter(d => !dailyLogs[d]);
    if (newDates.length === 0) return;
    Promise.all(
      newDates.map(d =>
        api.get(`/daily-log?date=${d}`).then(res => [d, res.data]).catch(() => [d, null])
      )
    ).then(results => {
      const newLogs = {};
      results.forEach(([d, data]) => {
        if (data && (data.stressLevel || data.sleepQuality || data.hydration || data.exerciseLevel || data.alcoholIntake || data.medicationTaken != null)) {
          newLogs[d] = data;
        }
      });
      if (Object.keys(newLogs).length > 0) {
        setDailyLogs(prev => ({ ...prev, ...newLogs }));
      }
    });
  }, [entries, dailyLogs]);

  const grouped = groupByDate(entries);
  const weekDays = getWeekDays();
  const monthGrid = getMonthGrid(viewYear, viewMonth);

  // Helper to get daily log for a date group label
  const getDailyLogForGroup = (dateLabel) => {
    // Try to find a date string from entries in this group
    const groupEntries = grouped[dateLabel];
    if (!groupEntries || groupEntries.length === 0) return null;
    const d = new Date(groupEntries[0].timestamp);
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    return dailyLogs[key] || null;
  };

  const renderCheckinPills = (log) => {
    if (!log) return null;
    const pills = [];
    for (const [key, meta] of Object.entries(CHECKIN_LABELS)) {
      const val = log[key];
      if (val == null || val === '') continue;
      const display = meta.values[String(val)] || String(val);
      pills.push(
        <span key={key} style={{
          fontSize: '12px', padding: '3px 8px', borderRadius: '12px',
          background: '#EDE8E0', color: '#5C4A3A', border: '1px solid #D8CEBC',
          whiteSpace: 'nowrap',
        }}>
          {meta.label}: {display}
        </span>
      );
    }
    return pills.length > 0 ? pills : null;
  };

  return (
    <div className="page">
      <div className="page-header">
        <h1 className="page-title">History</h1>
      </div>

      <div className="container">
        {/* ── Week strip ── */}
        <div className="history-week-strip">
          <div className="history-week-days">
            {weekDays.map((date, i) => {
              const isSelected = isSameDay(selectedDate, date);
              const isToday = isSameDay(today, date);
              return (
                <button
                  key={i}
                  className={`history-day${isSelected ? ' selected' : ''}${isToday && !isSelected ? ' today' : ''}`}
                  onClick={() => handleDayClick(date)}
                >
                  <span className="history-day-label">{DAY_LABELS[i]}</span>
                  <span className="history-day-num">{date.getDate()}</span>
                </button>
              );
            })}
          </div>
          <button
            className={`history-calendar-toggle${calendarExpanded ? ' active' : ''}`}
            onClick={() => setCalendarExpanded(!calendarExpanded)}
            aria-label="Expand calendar"
          >
            <span style={{ fontSize: '18px' }}>&#x1F4C5;</span>
          </button>
        </div>

        {/* ── Expanded month calendar ── */}
        {calendarExpanded && (
          <div className="history-month-calendar">
            <div className="history-month-header">
              <button className="history-month-arrow" onClick={prevMonth}>&lsaquo;</button>
              <span className="history-month-title">{MONTH_NAMES[viewMonth]} {viewYear}</span>
              <button className="history-month-arrow" onClick={nextMonth}>&rsaquo;</button>
            </div>
            <div className="history-month-labels">
              {DAY_LABELS.map((l, i) => (
                <span key={i}>{l}</span>
              ))}
            </div>
            <div className="history-month-grid">
              {monthGrid.map((date, i) => {
                if (!date) return <span key={i} className="history-month-cell empty" />;
                const isSelected = isSameDay(selectedDate, date);
                const isToday = isSameDay(today, date);
                return (
                  <button
                    key={i}
                    className={`history-month-cell${isSelected ? ' selected' : ''}${isToday && !isSelected ? ' today' : ''}`}
                    onClick={() => handleMonthDayClick(date)}
                  >
                    {date.getDate()}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {selectedDate && (
          <button
            className="history-clear-filter"
            onClick={() => { setSelectedDate(null); fetchHistory(); }}
          >
            Show all &times;
          </button>
        )}

        {/* ── Entry list ── */}
        {loading ? (
          <div className="loading-container">
            <div className="spinner"></div>
          </div>
        ) : entries.length === 0 ? (
          <div className="card text-center">
            <p className="text-muted">
              {selectedDate ? 'No entries on this day.' : 'No entries yet. Start logging!'}
            </p>
          </div>
        ) : (
          Object.entries(grouped).map(([date, items]) => (
            <div key={date}>
              <p style={{ fontWeight: '600', color: '#7A5A44', marginBottom: '8px' }}>{date}</p>
              {(() => {
                const log = getDailyLogForGroup(date);
                const pills = renderCheckinPills(log);
                if (!pills) return null;
                return (
                  <div className="card" style={{
                    padding: '10px 14px', marginBottom: '10px',
                    background: '#FAF7F2', border: '1px solid #E8D9C8',
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="#7A5A44" strokeWidth="1.5" strokeLinecap="round">
                        <circle cx="8" cy="8" r="6" /><path d="M8 5v3l2 1" />
                      </svg>
                      <span style={{ fontSize: '13px', fontWeight: '600', color: '#7A5A44' }}>Daily Check-in</span>
                    </div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                      {pills}
                    </div>
                  </div>
                );
              })()}
              {items.map((item, index) => {
                const isExpanded = expandedId === item.id;
                const isEditing = editingId === item.id;
                const isConfirming = confirmDeleteId === item.id;
                return (
                  <div
                    key={item.id || index}
                    className={`card history-entry-card${isExpanded ? ' expanded' : ''}`}
                    onClick={() => handleCardClick(item)}
                  >
                    <div className="history-entry-row">
                      <span style={{ display: 'flex', alignItems: 'center' }}>
                        {item.type === 'meal' ? (
                          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#4A2E1F" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 8h1a4 4 0 0 1 0 8h-1"/><path d="M2 8h16v9a4 4 0 0 1-4 4H6a4 4 0 0 1-4-4V8z"/><line x1="6" y1="1" x2="6" y2="4"/><line x1="10" y1="1" x2="10" y2="4"/><line x1="14" y1="1" x2="14" y2="4"/></svg>
                        ) : (
                          <svg width="24" height="24" viewBox="0 0 24 24" fill="none"><path d="M12 2c-1 3-4 5-4 8a4 4 0 0 0 1 3c-2 0-4 2-4 4.5A5.5 5.5 0 0 0 10.5 23h3A5.5 5.5 0 0 0 19 17.5c0-2.5-2-4.5-4-4.5a4 4 0 0 0 1-3c0-3-3-5-4-8z" fill="#8B7355"/></svg>
                        )}
                      </span>
                      <div style={{ flex: 1 }}>
                        {item.type === 'meal' ? (
                          <>
                            <p style={{ margin: 0, fontWeight: '600' }}>
                              {item.foods?.map(f => f.name).join(', ') || 'Meal'}
                            </p>
                            <p style={{ margin: '4px 0 0', fontSize: '14px', color: '#7A5A44' }}>
                              {formatTime(item.timestamp)}
                            </p>
                          </>
                        ) : (
                          <>
                            <p style={{ margin: 0, fontWeight: '600', color: '#4A2E1F' }}>
                              Bowel Movement
                              {item.severity && (
                                <span style={{ marginLeft: '8px', fontWeight: '400', fontSize: '14px' }}>
                                  {BRISTOL_LABELS[item.severity] || item.severity}
                                </span>
                              )}
                            </p>
                            <p style={{ margin: '4px 0 0', fontSize: '14px', color: '#7A5A44' }}>
                              {formatTime(item.timestamp)}
                            </p>
                            {parseSymptoms(item).length > 0 && (
                              <div className="history-symptom-tags">
                                {parseSymptoms(item).map(s => (
                                  <span key={s} className="history-symptom-tag">
                                    {SYMPTOM_LABELS[s] || s}
                                  </span>
                                ))}
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    </div>

                    {isExpanded && !isEditing && !isConfirming && (
                      <div onClick={e => e.stopPropagation()}>
                        {/* Ingredient details for meals */}
                        {item.type === 'meal' && item.foods && item.foods.length > 0 && (
                          <div style={{ padding: '8px 0 12px', borderBottom: '1px solid #E8D9C8', marginBottom: '8px' }}>
                            {item.foods.map((food, fi) => {
                              // Parse ingredients — could be JSON string or array
                              let ings = [];
                              try {
                                ings = Array.isArray(food.ingredients) ? food.ingredients : (typeof food.ingredients === 'string' ? JSON.parse(food.ingredients) : []);
                              } catch(e) { ings = []; }
                              // Parse categories — could be JSON string or array
                              let cats = [];
                              try {
                                cats = Array.isArray(food.category) ? food.category : (typeof food.category === 'string' ? JSON.parse(food.category) : []);
                              } catch(e) { cats = []; }
                              // If category parsed to a non-array (e.g. plain string like "Bowl"), wrap or ignore
                              if (!Array.isArray(cats)) cats = [];

                              return (
                              <div key={fi} style={{ marginBottom: fi < item.foods.length - 1 ? '10px' : 0 }}>
                                <p style={{ margin: '0 0 4px', fontWeight: '600', fontSize: '14px', color: '#4A2E1F' }}>{food.name}</p>
                                {ings.length > 0 && (
                                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                                    {ings.map((ing, ii) => {
                                      const ingLower = (ing || '').toLowerCase();
                                      const isTrigger = cats.some(c => ['high-FODMAP', 'dairy', 'nightshades', 'artificial sweeteners'].includes(c)) &&
                                        ['garlic', 'onion', 'milk', 'cheese', 'cream', 'wheat', 'honey', 'mushroom', 'tomato', 'pepper', 'eggplant', 'sorbitol', 'aspartame'].some(t => ingLower.includes(t));
                                      return (
                                        <span
                                          key={ii}
                                          style={{
                                            fontSize: '12px',
                                            padding: '3px 8px',
                                            borderRadius: '12px',
                                            background: isTrigger ? '#FCEAE8' : '#f0edd4',
                                            color: isTrigger ? '#B8564A' : '#4A2E1F',
                                            border: `1px solid ${isTrigger ? '#e8c4be' : '#d4cfb8'}`,
                                            fontWeight: isTrigger ? '600' : '400',
                                          }}
                                        >
                                          {ing}
                                        </span>
                                      );
                                    })}
                                  </div>
                                )}
                                {cats.length > 0 && (
                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', marginTop: '4px' }}>
                                      {cats.map((cat, ci) => (
                                        <span key={ci} style={{
                                          fontSize: '11px', padding: '2px 6px', borderRadius: '8px',
                                          background: ['high-FODMAP', 'dairy', 'nightshades', 'artificial sweeteners'].includes(cat) ? '#fff0e0' : '#e8f0e0',
                                          color: ['high-FODMAP', 'dairy', 'nightshades', 'artificial sweeteners'].includes(cat) ? '#C4783C' : '#5a7a3a',
                                          fontWeight: '600',
                                        }}>
                                          {cat}
                                        </span>
                                      ))}
                                    </div>
                                )}
                              </div>
                            );
                            })}
                          </div>
                        )}
                        <div className="history-card-actions">
                          {item.type === 'poop' && (
                            <button
                              className="history-action-btn"
                              onClick={() => startEdit(item)}
                            >
                              Edit
                            </button>
                          )}
                          <button
                            className="history-action-btn delete"
                            onClick={() => setConfirmDeleteId(item.id)}
                          >
                            Delete
                          </button>
                        </div>
                      </div>
                    )}

                    {isConfirming && (
                      <div className="history-confirm-row" onClick={e => e.stopPropagation()}>
                        <span style={{ fontSize: '14px', color: '#7A5A44' }}>Delete this entry?</span>
                        <button
                          className="history-action-btn delete"
                          onClick={() => handleDelete(item)}
                          disabled={actionLoading}
                        >
                          {actionLoading ? '...' : 'Yes, delete'}
                        </button>
                        <button
                          className="history-action-btn"
                          onClick={() => setConfirmDeleteId(null)}
                        >
                          Cancel
                        </button>
                      </div>
                    )}

                    {isEditing && item.type === 'poop' && (
                      <div className="history-edit-severity" onClick={e => e.stopPropagation()}>
                        {BRISTOL_EDIT_OPTIONS.map(s => (
                          <button
                            key={s.val}
                            className={`history-severity-btn${editSeverity === s.val ? ' active' : ''}`}
                            onClick={() => setEditSeverity(s.val)}
                          >
                            {s.label}
                          </button>
                        ))}
                        <p style={{ width: '100%', margin: '8px 0 4px', fontSize: '13px', fontWeight: '600', color: '#7A5A44' }}>Symptoms</p>
                        <div className="symptom-chips" style={{ width: '100%' }}>
                          {SYMPTOM_OPTIONS.map(s => (
                            <button
                              key={s.val}
                              className={`symptom-chip${editSymptoms.includes(s.val) ? ' active' : ''}`}
                              onClick={() => toggleEditSymptom(s.val)}
                            >
                              {s.label}
                            </button>
                          ))}
                        </div>
                        <div className="history-edit-btns">
                          <button
                            className="history-action-btn save"
                            onClick={() => savePoopEdit(item)}
                            disabled={actionLoading}
                          >
                            {actionLoading ? '...' : 'Save'}
                          </button>
                          <button className="history-action-btn" onClick={cancelEdit}>Cancel</button>
                        </div>
                      </div>
                    )}

                  </div>
                );
              })}
            </div>
          ))
        )}

        {hasMore && !selectedDate && (
          <button
            onClick={() => fetchHistory(nextCursor)}
            disabled={loadingMore}
            style={{
              display: 'block',
              width: '100%',
              padding: '12px',
              marginTop: '8px',
              background: 'none',
              border: '2px solid #E8D9C8',
              borderRadius: '16px',
              color: '#7A5A44',
              fontSize: '15px',
              fontWeight: '600',
              cursor: 'pointer',
              fontFamily: 'inherit',
              opacity: loadingMore ? 0.6 : 1
            }}
          >
            {loadingMore ? 'Loading...' : 'Load more'}
          </button>
        )}
      </div>
    </div>
  );
}

export default History;
